import React from 'react';
import PropTypes from 'prop-types';
import {
  injectIntl,
  intlShape,
  FormattedMessage,
} from '@edx/frontend-platform/i18n';
import { Form, Hyperlink } from '@openedx/paragon';
import TextareaAutosize from 'react-textarea-autosize';

import CourseUploadImage from '../../generic/course-upload-image';
import { WysiwygEditor } from '../../generic/WysiwygEditor';
import DynamicStringList from '../../generic/dynamic-string-list';
import SectionSubHeader from '../../generic/section-sub-header';
import IntroductionVideo from './introduction-video';
import ExtendedCourseDetails from './extended-course-details';
import messages from './messages';

const IntroducingSection = ({
  intl,
  title,
  subtitle,
  duration,
  overview,
  introVideo,
  description,
  aboutSidebarHtml,
  shortDescription,
  aboutPageEditable,
  sidebarHtmlEnabled,
  lmsLinkForAboutPage,
  courseImageAssetPath,
  bannerImageAssetPath,
  shortDescriptionEditable,
  enableExtendedCourseDetails,
  videoThumbnailImageAssetPath,
  titleTag,
  metaDescriptionTags,
  additionalInfo,
  iconsBadges,
  faqs,
  disclaimers,
  onChange,
}) => {
  const overviewHelpText = (
    <FormattedMessage
      id="course-authoring.schedule-section.introducing.course-overview.help-text"
      defaultMessage="Introductions, prerequisites, FAQs that are used on {hyperlink} (formatted in HTML)"
      values={{
        hyperlink: (
          <Hyperlink
            destination={lmsLinkForAboutPage}
            target="_blank"
            showLaunchIcon={false}
          >
            {intl.formatMessage(messages.courseAboutHyperlink)}
          </Hyperlink>
        ),
      }}
    />
  );

  const aboutSidebarHelpText = (
    <FormattedMessage
      id="course-authoring.schedule-section.introducing.about-sidebar.help-text"
      defaultMessage="Custom sidebar content for {hyperlink} (formatted in HTML)"
      values={{
        hyperlink: (
          <Hyperlink
            destination={lmsLinkForAboutPage}
            target="_blank"
            showLaunchIcon={false}
          >
            {intl.formatMessage(messages.courseAboutHyperlink)}
          </Hyperlink>
        ),
      }}
    />
  );

  return (
    <section className="section-container introducing-section">
      {aboutPageEditable && (
        <SectionSubHeader
          title={intl.formatMessage(messages.introducingTitle)}
          description={intl.formatMessage(messages.introducingDescription)}
        />
      )}
      {enableExtendedCourseDetails && (
        <ExtendedCourseDetails
          title={title}
          subtitle={subtitle}
          duration={duration}
          description={description}
          onChange={onChange}
        />
      )}
      {shortDescriptionEditable && (
        <Form.Group className="form-group-custom">
          <Form.Label>
            {intl.formatMessage(messages.courseShortDescriptionLabel)}
          </Form.Label>
          <Form.Control
            as="textarea"
            value={shortDescription}
            name="shortDescription"
            onChange={(e) => onChange(e.target.value, 'shortDescription')}
            aria-label={intl.formatMessage(
              messages.courseShortDescriptionAriaLabel,
            )}
            maxLength={150}
          />
          <Form.Control.Feedback>
            {intl.formatMessage(messages.courseShortDescriptionHelpText)}
          </Form.Control.Feedback>
        </Form.Group>
      )}
      {aboutPageEditable && (
        <>
          <Form.Group className="form-group-custom">
            <Form.Label>{intl.formatMessage(messages.courseOverviewLabel)}</Form.Label>
            <WysiwygEditor
              initialValue={overview}
              onChange={(value) => onChange(value, 'overview')}
            />
            <Form.Control.Feedback>{overviewHelpText}</Form.Control.Feedback>
          </Form.Group>
          {sidebarHtmlEnabled && (
            <Form.Group className="form-group-custom">
              <Form.Label>{intl.formatMessage(messages.courseAboutSidebarLabel)}</Form.Label>
              <WysiwygEditor
                initialValue={aboutSidebarHtml}
                onChange={(value) => onChange(value, 'aboutSidebarHtml')}
              />
              <Form.Control.Feedback>{aboutSidebarHelpText}</Form.Control.Feedback>
            </Form.Group>
          )}
          <CourseUploadImage
            label={intl.formatMessage(messages.courseCardImageLabel)}
            identifierFieldText={intl.formatMessage(messages.courseCardImageIdentifierText)}
            assetImagePath={courseImageAssetPath}
            assetImageField="courseImageAssetPath"
            imageNameField="courseImageName"
            showImageBodyText
            onChange={onChange}
          />
          <IntroductionVideo introVideo={introVideo} onChange={onChange} />
          <Form.Group className="form-group-custom">
            <Form.Label>Course Title Tag</Form.Label>
            <Form.Control
              value={titleTag}
              onChange={(e) => onChange(e.target.value, 'titleTag')}
            />
          </Form.Group>
          <Form.Group className="form-group-custom">
            <Form.Label>Course Meta Description Tags</Form.Label>
            <WysiwygEditor
              initialValue={metaDescriptionTags}
              onChange={(value) => onChange(value, 'metaDescriptionTags')}
            />
          </Form.Group>
          <Form.Group className="form-group-custom">
            <Form.Label>Additional Info</Form.Label>
            <Form.Control
              as={TextareaAutosize}
              value={additionalInfo}
              onChange={(e) => onChange(e.target.value, 'additionalInfo')}
            />
          </Form.Group>
          <Form.Group className="form-group-custom">
            <Form.Label>Learning Icons and Badges</Form.Label>
            <WysiwygEditor
              initialValue={iconsBadges}
              onChange={(value) => onChange(value, 'iconsBadges')}
            />
          </Form.Group>
          <Form.Group className="form-group-custom">
            <Form.Label>Frequently Asked Questions</Form.Label>
            <WysiwygEditor
              initialValue={faqs}
              onChange={(value) => onChange(value, 'faqs')}
            />
          </Form.Group>
          <Form.Group className="form-group-custom">
            <Form.Label>Disclaimers</Form.Label>
            <DynamicStringList
              items={disclaimers || []}
              fieldKey="disclaimers"
              label="Disclaimer"
              addLabel="Add Disclaimer"
              deleteLabel="Delete"
              placeholder="Enter disclaimer text"
              onChange={onChange}
            />
          </Form.Group>
        </>
      )}
      {enableExtendedCourseDetails && (
        <>
          <CourseUploadImage
            label={intl.formatMessage(messages.courseBannerImageLabel)}
            identifierFieldText={intl.formatMessage(messages.courseBannerImageInsertText)}
            assetImagePath={bannerImageAssetPath}
            assetImageField="bannerImageAssetPath"
            imageNameField="bannerImageName"
            showImageBodyText
            onChange={onChange}
          />
          <CourseUploadImage
            label={intl.formatMessage(messages.courseVideoThumbnailLabel)}
            identifierFieldText={intl.formatMessage(messages.courseVideoThumbnailInsertText)}
            assetImagePath={videoThumbnailImageAssetPath}
            assetImageField="videoThumbnailImageAssetPath"
            imageNameField="videoThumbnailImageName"
            showImageBodyText
            onChange={onChange}
          />
        </>
      )}
    </section>
  );
};

IntroducingSection.defaultProps = {
  title: '',
  subtitle: '',
  duration: '',
  description: '',
  introVideo: '',
  shortDescription: '',
  aboutSidebarHtml: '',
  courseImageAssetPath: '',
  bannerImageAssetPath: '',
  videoThumbnailImageAssetPath: '',
  overview: '',
  titleTag: '',
  metaDescriptionTags: '',
  additionalInfo: '',
  iconsBadges: '',
  faqs: '',
  disclaimers: [],
};

IntroducingSection.propTypes = {
  intl: intlShape.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  duration: PropTypes.string,
  description: PropTypes.string,
  overview: PropTypes.string,
  introVideo: PropTypes.string,
  aboutSidebarHtml: PropTypes.string,
  shortDescription: PropTypes.string,
  aboutPageEditable: PropTypes.bool.isRequired,
  sidebarHtmlEnabled: PropTypes.bool.isRequired,
  lmsLinkForAboutPage: PropTypes.string.isRequired,
  courseImageAssetPath: PropTypes.string,
  bannerImageAssetPath: PropTypes.string,
  shortDescriptionEditable: PropTypes.bool.isRequired,
  enableExtendedCourseDetails: PropTypes.bool.isRequired,
  videoThumbnailImageAssetPath: PropTypes.string,
  titleTag: PropTypes.string,
  metaDescriptionTags: PropTypes.string,
  additionalInfo: PropTypes.string,
  iconsBadges: PropTypes.string,
  faqs: PropTypes.string,
  disclaimers: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

export default injectIntl(IntroducingSection);
